import { Clock, Send, Trash2 } from "lucide-react";
import { formatSnoozeUntil } from "@/lib/snooze/snoozeDateTime";
import type { MailScheduledSendEntry } from "@/lib/api/mailScheduledSend";

type Props = {
  entry: MailScheduledSendEntry;
  accountEmail: string;
  onSendNow: () => void;
  onCancel: () => void;
  busy?: boolean;
};

export function ScheduledSendReader({
  entry,
  accountEmail,
  onSendNow,
  onCancel,
  busy = false,
}: Props) {
  const payload = entry.payload;
  const scheduledLabel = formatSnoozeUntil(entry.sendAt);
  const bodyText = payload?.bodyText?.trim() || "";

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      <div className="flex shrink-0 items-center justify-end gap-1 border-b border-[var(--mail-border-soft)] px-3 py-2">
        <button
          type="button"
          onClick={onSendNow}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-[var(--mail-accent)] hover:bg-[var(--mail-accent-soft)] disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          Envoyer maintenant
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Annuler
        </button>
      </div>

      <div className="shrink-0 space-y-2 border-b border-[var(--mail-border-soft)] px-5 py-4">
        <div className="inline-flex items-center gap-2 rounded-full bg-[var(--mail-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--mail-accent)]">
          <Clock className="h-3.5 w-3.5" />
          Envoi programmé · {scheduledLabel}
        </div>

        <h2 className="text-lg font-semibold leading-snug">
          {entry.subject.trim() || "(Sans objet)"}
        </h2>

        <p className="text-sm text-[var(--mail-muted)]">
          De <span className="text-[var(--mail-text)]">{accountEmail}</span>
        </p>
        {payload?.toRaw || payload?.to ? (
          <p className="text-sm text-[var(--mail-muted)]">
            À{" "}
            <span className="text-[var(--mail-text)]">
              {payload.toRaw ?? payload.to}
            </span>
          </p>
        ) : null}
        {payload?.cc ? (
          <p className="text-sm text-[var(--mail-muted)]">
            Cc <span className="text-[var(--mail-text)]">{payload.cc}</span>
          </p>
        ) : null}

        {entry.error ? (
          <p className="mail-alert-error px-3 py-2 text-sm" role="alert">
            {entry.error}
          </p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap px-5 py-4 text-sm leading-relaxed text-[var(--mail-text)]">
        {bodyText || (
          <p className="text-[var(--mail-muted)]">Corps HTML — ouvrez après envoi dans Envoyés.</p>
        )}
      </div>
    </div>
  );
}
